// RabbiUploadPage.tsx - Page for Rabbi to select Parasha, list Psukim, and upload or record audio
// filepath: client/src/newApp/pages/RabbiUploadPage.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../admin/i18n';
import tora from '../../data/torah.json';
import { Pasuk } from '../../data/psukim';
import parashaMap from '../../parasha_map.json';
import { convertAudioBlobToMp3 } from '../utils/mp3Converter';
import { FIREBASE_FUNCTIONS, callCloudFunction } from '../utils/firebaseFunctions';
import {
  getRabbiAudioUrl,
  getRabbiSpecialAudioUrl,
  RabbiSpecialAudioType,
} from '../components/utils';
import BottomNav from '../components/BottomNav';
import SpinnerOverlay from '../components/SpinnerOverlay';
import {
  trackAudioPlayback,
  trackPageView,
  trackParashaSelection,
  trackRabbiAudioEvent,
} from '../utils/analytics';
import { renderHebrewWithShvaNa } from '../utils/hebrewTextUtils';
import { numberToHebrewLetters } from '../utils/hebrewNumbers';

interface TorahPasuk extends Pasuk {
  parasha?: string; // torah.json seems to include parasha field
}

const parashaNameMap: Record<string, string> = (parashaMap as any[]).reduce(
  (acc, p) => {
    acc[p.english] = p.hebrew;
    return acc;
  },
  {} as Record<string, string>,
);

// Module-level cache for checkRabbiAudioExists so we don't call the API multiple times for the same parasha/rabbi
let s3ExistenceCacheModule: Record<string, Record<string, boolean>> = {};

// Group parashot by Torah book (same as BirthDatePage)
const parashotByBook = [
  {
    bookEnglish: 'Bereshit (Genesis)',
    bookHebrew: 'בראשית',
    parashot: (parashaMap as any[]).slice(0, 12), // Bereshit to Vayechi
  },
  {
    bookEnglish: 'Shemot (Exodus)',
    bookHebrew: 'שמות',
    parashot: (parashaMap as any[]).slice(12, 23), // Shemot to Pekudei
  },
  {
    bookEnglish: 'Vayikra (Leviticus)',
    bookHebrew: 'ויקרא',
    parashot: (parashaMap as any[]).slice(23, 33), // Vayikra to Bechukotai
  },
  {
    bookEnglish: 'Bamidbar (Numbers)',
    bookHebrew: 'במדבר',
    parashot: (parashaMap as any[]).slice(33, 43), // Bamidbar to Masei
  },
  {
    bookEnglish: 'Devarim (Deuteronomy)',
    bookHebrew: 'דברים',
    parashot: (parashaMap as any[]).slice(43), // Devarim to V'Zot HaBerachah
  },
];

interface RabbiInfo {
  nameHe?: string;
  nameEn?: string;
  nusach?: string;
  firstName?: string;
  lastName?: string;
  key?: string;
  id?: string;
}

const RabbiUploadPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isHebrew = i18n.language?.startsWith('he');
  const [rabbiInfo, setRabbiInfo] = useState<RabbiInfo | null>(null);
  const [selectedParasha, setSelectedParasha] = useState<string>('');
  const [psukim, setPsukim] = useState<TorahPasuk[]>([]);
  const [selectedPasuk, setSelectedPasuk] = useState<TorahPasuk | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [, setAudioChunks] = useState<Blob[]>([]);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [statusType, setStatusType] = useState<'success' | 'error' | 'loading' | 'info'>('info');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const specialFileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingFiles, setPendingFiles] = useState<Record<number, File>>({});
  const [pendingRecordingBlobs, setPendingRecordingBlobs] = useState<Record<number, Blob>>({});
  const [s3ExistenceMap, setS3ExistenceMap] = useState<Record<string, boolean>>({});
  const [checkingS3, setCheckingS3] = useState(false);
  const [shouldCheckS3, setShouldCheckS3] = useState(false);
  const s3CheckCacheRef = useRef<Record<string, Record<string, boolean>>>({});
  const invalidateS3CacheRef = useRef(false);
  const checkInFlightRef = useRef<string | null>(null);
  const psukimRef = useRef<TorahPasuk[]>([]);
  const audioRefs = useRef<Record<number, HTMLAudioElement | null>>({});
  const [playingAudioId, setPlayingAudioId] = useState<number | null>(null);
  const objectUrlRefs = useRef<Record<number, string>>({});
  const [savingPasukId, setSavingPasukId] = useState<number | null>(null);
  const [pendingSpecialFiles, setPendingSpecialFiles] = useState<
    Record<RabbiSpecialAudioType, File | undefined>
  >({
    shema: undefined,
    birchot_hatorah: undefined,
    birchot_hatorah_before: undefined,
    birchot_hatorah_after: undefined,
  });
  const [pendingSpecialBlobs, setPendingSpecialBlobs] = useState<
    Record<RabbiSpecialAudioType, Blob | undefined>
  >({
    shema: undefined,
    birchot_hatorah: undefined,
    birchot_hatorah_before: undefined,
    birchot_hatorah_after: undefined,
  });
  const [recordingSpecial, setRecordingSpecial] = useState<RabbiSpecialAudioType | null>(null);
  const [savingSpecial, setSavingSpecial] = useState<RabbiSpecialAudioType | null>(null);
  const [playingSpecial, setPlayingSpecial] = useState<RabbiSpecialAudioType | null>(null);
  const [specialFileUploadTarget, setSpecialFileUploadTarget] =
    useState<RabbiSpecialAudioType | null>(null);
  const specialAudioRefs = useRef<Record<RabbiSpecialAudioType, HTMLAudioElement | null>>({
    shema: null,
    birchot_hatorah: null,
    birchot_hatorah_before: null,
    birchot_hatorah_after: null,
  });
  const specialObjectUrlRefs = useRef<Record<RabbiSpecialAudioType, string | undefined>>({
    shema: undefined,
    birchot_hatorah: undefined,
    birchot_hatorah_before: undefined,
    birchot_hatorah_after: undefined,
  });
  const buildEventProps = (pasuk?: TorahPasuk) => ({
    rabbi_id: rabbiInfo?.id || rabbiInfo?.key,
    rabbi_nusach: rabbiInfo?.nusach,
    parasha: selectedParasha || pasuk?.parasha,
    pasuk_id: pasuk?.id ?? selectedPasuk?.id,
    book: pasuk?.book ?? selectedPasuk?.book,
    chapter: pasuk?.chapter ?? selectedPasuk?.chapter,
    pasuk_number: pasuk?.pasuk ?? selectedPasuk?.pasuk,
  });

  // Load rabbi info from localStorage on mount
  useEffect(() => {
    const storedRabbiInfo = localStorage.getItem('rabbiInfo');
    const token = localStorage.getItem('rabbiToken');
    if (storedRabbiInfo) {
      try {
        const parsed = JSON.parse(storedRabbiInfo);
        if (!parsed.key && token) {
          parsed.key = token;
        }
        if (!parsed.id && token) {
          parsed.id = token;
        }
        setRabbiInfo(parsed);
      } catch (e) {
        console.error('Failed to parse rabbi info:', e);
      }
    } else if (token) {
      // Fallback: if we only have the token, still allow URL building
      setRabbiInfo({ id: token, key: token });
    }
  }, []);

  useEffect(() => {
    trackPageView('Rabbi Upload', {
      rabbi_id: rabbiInfo?.id || rabbiInfo?.key,
      rabbi_nusach: rabbiInfo?.nusach,
    });
  }, [rabbiInfo]);

  useEffect(() => {
    if (selectedParasha) {
      const filtered = (tora as any[]).filter((p) => p.parasha === selectedParasha);
      setPsukim(filtered as TorahPasuk[]);
      setShouldCheckS3(true); // initial page/parasha load
    } else {
      setPsukim([]);
      setS3ExistenceMap({});
    }
  }, [selectedParasha]);

  useEffect(() => {
    psukimRef.current = psukim;
  }, [psukim]);

  // Check S3 existence for psukim and for special types (shema, birchot_hatorah); use cache to avoid repeated calls
  useEffect(() => {
    const checkS3Existence = async () => {
      const token = localStorage.getItem('rabbiToken');
      if (!token) {
        if (shouldCheckS3) setShouldCheckS3(false);
        return;
      }
      if (!shouldCheckS3 && !rabbiInfo) return;

      const psukimList = psukimRef.current;
      const psukimIds =
        psukimList.length > 0 ? psukimList.map((p) => p.id).sort((a, b) => a - b) : [];
      const cacheKey = `${token}-${psukimIds.length ? psukimIds.join(',') : 'no-psukim'}`;

      if (invalidateS3CacheRef.current) {
        delete s3CheckCacheRef.current[cacheKey];
        delete s3ExistenceCacheModule[cacheKey];
        invalidateS3CacheRef.current = false;
      }

      const cached = s3ExistenceCacheModule[cacheKey] ?? s3CheckCacheRef.current[cacheKey];
      if (cached != null) {
        setS3ExistenceMap(cached);
        setCheckingS3(false);
        setShouldCheckS3(false);
        return;
      }

      if (checkInFlightRef.current === cacheKey) {
        setShouldCheckS3(false);
        return;
      }
      checkInFlightRef.current = cacheKey;
      setCheckingS3(true);
      try {
        const CHECK_ENDPOINT = FIREBASE_FUNCTIONS.checkRabbiAudioExists();
        const resp = await callCloudFunction('checkRabbiAudioExists', CHECK_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            psukim: psukimList.length > 0 ? psukimList : [],
            specialTypes: ['shema', 'birchot_hatorah'],
          }),
        });

        if (resp.ok) {
          const data = await resp.json();
          if (data.existence) {
            const existence = data.existence as Record<string, boolean>;
            s3ExistenceCacheModule[cacheKey] = existence;
            s3CheckCacheRef.current[cacheKey] = existence;
            setS3ExistenceMap((prev) => ({ ...prev, ...existence }));
          }
        } else {
          console.error('Failed to check S3 existence:', resp.status);
        }
      } catch (err) {
        console.error('Error checking S3 existence:', err);
      } finally {
        checkInFlightRef.current = null;
        setCheckingS3(false);
        setShouldCheckS3(false);
      }
    };

    void checkS3Existence();
  }, [shouldCheckS3, rabbiInfo]);

  // Attach playable URLs for psukim that already exist in storage
  useEffect(() => {
    if (!rabbiInfo?.nusach) return;
    const rabbiKey = rabbiInfo.key || localStorage.getItem('rabbiToken') || rabbiInfo.id || '';
    if (!rabbiKey) return;
    if (!psukim.length) return;

    setPsukim((prev) =>
      prev.map((p) => {
        if (!s3ExistenceMap[p.id]) return p;
        if (p.audioUrl) return p;
        if (pendingFiles[p.id] || pendingRecordingBlobs[p.id]) return p;
        const url = getRabbiAudioUrl(p, rabbiInfo.nusach as string, rabbiInfo.key);
        return url ? { ...p, audioUrl: url } : p;
      }),
    );
  }, [psukim.length, s3ExistenceMap, rabbiInfo, pendingFiles, pendingRecordingBlobs]);

  // Cleanup object URLs when pending files change or component unmounts
  useEffect(() => {
    return () => {
      // Cleanup all object URLs on unmount
      Object.values(objectUrlRefs.current).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      objectUrlRefs.current = {};
    };
  }, []);

  useEffect(() => {
    // Cleanup object URLs for pasukim that no longer have pending files
    Object.keys(objectUrlRefs.current).forEach((pasukIdStr) => {
      const pasukId = Number(pasukIdStr);
      if (
        !pendingFiles[pasukId] &&
        !pendingRecordingBlobs[pasukId] &&
        objectUrlRefs.current[pasukId]
      ) {
        URL.revokeObjectURL(objectUrlRefs.current[pasukId]);
        delete objectUrlRefs.current[pasukId];
      }
    });
  }, [pendingFiles, pendingRecordingBlobs]);

  useEffect(() => {
    (['shema', 'birchot_hatorah'] as RabbiSpecialAudioType[]).forEach((type) => {
      if (
        !pendingSpecialFiles[type] &&
        !pendingSpecialBlobs[type] &&
        specialObjectUrlRefs.current[type]
      ) {
        URL.revokeObjectURL(specialObjectUrlRefs.current[type]!);
        specialObjectUrlRefs.current[type] = undefined;
      }
    });
  }, [pendingSpecialFiles, pendingSpecialBlobs]);

  useEffect(() => {
    const refs = specialObjectUrlRefs.current;
    return () => {
      (['shema', 'birchot_hatorah'] as RabbiSpecialAudioType[]).forEach((type) => {
        if (refs[type]) {
          URL.revokeObjectURL(refs[type]!);
          refs[type] = undefined;
        }
      });
    };
  }, []);

  // Use environment-aware Firebase Functions endpoint
  const RABBI_AUDIO_UPLOAD_ENDPOINT = FIREBASE_FUNCTIONS.uploadRabbiAudio();

  const uploadRabbiAudioFile = async (
    book: string,
    chapter: string,
    pasuk: number,
    audioFile: File,
  ): Promise<string> => {
    // Convert to mp3 if not already mp3
    let mp3File = audioFile;
    if (audioFile.type !== 'audio/mp3' && audioFile.type !== 'audio/mpeg') {
      // convertAudioBlobToMp3 needs a Blob, returns a Promise<File>
      const mp3Blob = await convertAudioBlobToMp3(audioFile);
      mp3File = new File([mp3Blob], audioFile.name.replace(/\.[^/.]+$/, '') + '.mp3', {
        type: 'audio/mp3',
      });
    }
    // Convert the mp3File to base64 string to send in JSON payload
    const fileToBase64 = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Remove the data:*/*;base64, prefix if present
          const base64Data = result.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    };

    const base64Audio = await fileToBase64(mp3File);

    // Get bearer token from localStorage
    const token = localStorage.getItem('rabbiToken');
    if (!token) {
      throw new Error('No authentication token found. Please login first.');
    }

    const resp = await callCloudFunction('uploadRabbiAudio', RABBI_AUDIO_UPLOAD_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        book,
        chapter,
        pasuk,
        audioBase64: base64Audio,
        audioName: mp3File.name,
        audioType: mp3File.type,
      }),
    });

    const data = await resp.json();
    if (!resp.ok) {
      if (data.errorCode === 100) {
        setStatusMessage(t('rabbi_upload_pasuk_not_found') as string);
        setStatusType('error');
        return '';
      }
      throw new Error(`Upload failed: ${resp.status} ${await resp.text()}`);
    }

    if (!data.url) throw new Error('No URL returned');
    return data.url as string;
  };

  // Case 2: Upload from a Blob (e.g., from recording)
  const uploadRabbiPasukAudioFromBlob = async (
    book: string,
    chapter: string,
    pasukId: number,
    blob: Blob,
  ): Promise<string> => {
    const arrayBuffer = await blob.arrayBuffer();
    const fileType = blob.type || 'audio/webm';
    const file = new File([arrayBuffer], `recording-${Date.now()}.webm`, {
      type: fileType,
    });
    return uploadRabbiAudioFile(book, chapter, pasukId, file);
  };

  const uploadRabbiSpecialAudio = async (
    specialType: RabbiSpecialAudioType,
    fileOrBlob: File | Blob,
  ): Promise<string> => {
    const file =
      fileOrBlob instanceof File
        ? fileOrBlob
        : new File([fileOrBlob], `recording-${specialType}-${Date.now()}.webm`, {
            type: fileOrBlob.type || 'audio/webm',
          });
    let mp3File = file;
    if (file.type !== 'audio/mp3' && file.type !== 'audio/mpeg') {
      const mp3Blob = await convertAudioBlobToMp3(file);
      mp3File = new File([mp3Blob], file.name.replace(/\.[^/.]+$/, '') + '.mp3', {
        type: 'audio/mp3',
      });
    }
    const fileToBase64 = (f: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1] || '');
        };
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });
    const base64Audio = await fileToBase64(mp3File);
    const token = localStorage.getItem('rabbiToken');
    if (!token) throw new Error('No authentication token found.');
    const resp = await fetch(RABBI_AUDIO_UPLOAD_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ specialType, audioBase64: base64Audio }),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data.error || 'Upload failed');
    if (!data.url) throw new Error('No URL returned');
    return data.url as string;
  };

  const deleteRabbiSpecialAudio = async (specialType: RabbiSpecialAudioType): Promise<void> => {
    const token = localStorage.getItem('rabbiToken');
    if (!token) throw new Error('No authentication token found.');
    const DELETE_ENDPOINT = FIREBASE_FUNCTIONS.deleteRabbiAudio();
    const resp = await fetch(DELETE_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ specialType }),
    });
    if (!resp.ok) {
      let data: { error?: string } = {};
      try {
        data = await resp.json();
      } catch {
        // ignore
      }
      throw new Error(data.error || 'Delete failed');
    }
  };

  const startRecordingSpecial = async (type: RabbiSpecialAudioType) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const localChunks: Blob[] = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) localChunks.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(localChunks, { type: 'audio/webm' });
        setPendingSpecialBlobs((prev) => ({ ...prev, [type]: blob }));
        setPendingSpecialFiles((prev) => ({ ...prev, [type]: undefined }));
        setRecordingSpecial(null);
        setStatusMessage(t('rabbi_upload_recording_ready') as string);
        setStatusType('info');
      };
      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingSpecial(type);
      setStatusMessage(t('rabbi_upload_recording') as string);
      setStatusType('loading');
    } catch (err) {
      console.error(err);
      setRecordingSpecial(null);
      setStatusMessage(t('rabbi_upload_mic_denied') as string);
      setStatusType('error');
    }
  };

  const stopRecordingSpecial = () => {
    if (mediaRecorder && recordingSpecial) {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach((t) => t.stop());
      setIsRecording(false);
      setMediaRecorder(null);
      setRecordingSpecial(null);
    }
  };

  const handleSendSpecial = async (type: RabbiSpecialAudioType) => {
    const file = pendingSpecialFiles[type];
    const blob = pendingSpecialBlobs[type];
    if (!file && !blob) return;
    setSavingSpecial(type);
    setStatusMessage(t('rabbi_upload_uploading_recording') as string);
    setStatusType('loading');
    try {
      const url = await uploadRabbiSpecialAudio(type, file || blob!);
      if (url) {
        setStatusMessage(t('rabbi_upload_uploaded_success') as string);
        setStatusType('success');
        setS3ExistenceMap((prev) => ({ ...prev, [type]: true }));
        invalidateS3CacheRef.current = true;
        setPendingSpecialFiles((prev) => ({ ...prev, [type]: undefined }));
        setPendingSpecialBlobs((prev) => ({ ...prev, [type]: undefined }));
      }
    } catch (err) {
      console.error(err);
      setStatusMessage(t('rabbi_upload_upload_failed') as string);
      setStatusType('error');
    } finally {
      setSavingSpecial(null);
    }
  };

  const handleDeleteSpecial = async (type: RabbiSpecialAudioType) => {
    setStatusMessage(t('rabbi_upload_deleting_audio') as string);
    setStatusType('loading');
    try {
      await deleteRabbiSpecialAudio(type);
      setS3ExistenceMap((prev) => ({ ...prev, [type]: false }));
      invalidateS3CacheRef.current = true;
      setPendingSpecialFiles((prev) => ({ ...prev, [type]: undefined }));
      setPendingSpecialBlobs((prev) => ({ ...prev, [type]: undefined }));
      if (playingSpecial === type) {
        specialAudioRefs.current[type]?.pause();
        setPlayingSpecial(null);
      }
      setStatusMessage(t('rabbi_upload_audio_deleted') as string);
      setStatusType('success');
    } catch (err) {
      console.error(err);
      setStatusMessage(t('rabbi_upload_delete_failed') as string);
      setStatusType('error');
    }
  };

  const handlePlaybackSpecial = (type: RabbiSpecialAudioType) => {
    const audio = specialAudioRefs.current[type];
    if (!audio) return;
    if (playingSpecial === type && !audio.paused) {
      audio.pause();
      setPlayingSpecial(null);
    } else {
      Object.keys(specialAudioRefs.current).forEach((k) => {
        const other = specialAudioRefs.current[k as RabbiSpecialAudioType];
        if (other && k !== type) {
          other.pause();
          other.currentTime = 0;
        }
      });
      audio
        .play()
        .then(() => setPlayingSpecial(type))
        .catch(console.error);
    }
  };

  const handleSpecialFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !specialFileUploadTarget) return;
    setPendingSpecialFiles((prev) => ({
      ...prev,
      [specialFileUploadTarget]: file,
    }));
    setPendingSpecialBlobs((prev) => ({
      ...prev,
      [specialFileUploadTarget]: undefined,
    }));
    setSpecialFileUploadTarget(null);
    setStatusMessage(t('rabbi_upload_file_selected') as string);
    setStatusType('info');
    e.target.value = '';
  };

  const startRecording = async (pasuk?: TorahPasuk) => {
    const pasukToUse = pasuk || selectedPasuk;
    if (!pasukToUse) return;
    // Ensure the pasuk is selected
    if (!selectedPasuk || selectedPasuk.id !== pasukToUse.id) {
      setSelectedPasuk(pasukToUse);
    }
    const pasukId = pasukToUse.id;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const localChunks: Blob[] = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) localChunks.push(e.data);
      };
      recorder.onstop = async () => {
        setAudioChunks(localChunks);
        const blob = new Blob(localChunks, { type: 'audio/webm' });
        setPendingRecordingBlobs((prev) => ({ ...prev, [pasukId]: blob }));
        setPendingFiles((prev) => {
          const newFiles = { ...prev };
          delete newFiles[pasukId];
          return newFiles;
        });
        setStatusMessage(t('rabbi_upload_recording_ready') as string);
        setStatusType('info');
        trackRabbiAudioEvent('record_stop', {
          ...buildEventProps(pasukToUse),
          recording_size: blob.size,
        });
      };
      recorder.start();
      trackRabbiAudioEvent('record_start', {
        ...buildEventProps(pasukToUse),
        method: 'microphone',
      });
      setMediaRecorder(recorder);
      setIsRecording(true);
      setStatusMessage(t('rabbi_upload_recording') as string);
      setStatusType('loading');
    } catch (err) {
      console.error(err);
      setStatusMessage(t('rabbi_upload_mic_denied') as string);
      setStatusType('error');
      trackRabbiAudioEvent('record_error', {
        ...buildEventProps(pasukToUse),
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach((t) => t.stop());
      setIsRecording(false);
      setMediaRecorder(null);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedPasuk) return;
    const file = e.target.files?.[0];
    if (!file) return;
    const pasukId = selectedPasuk.id;
    setPendingFiles((prev) => ({ ...prev, [pasukId]: file }));
    setPendingRecordingBlobs((prev) => {
      const newBlobs = { ...prev };
      delete newBlobs[pasukId];
      return newBlobs;
    });
    setStatusMessage(t('rabbi_upload_file_selected') as string);
    setStatusType('info');
    trackRabbiAudioEvent('file_selected', {
      ...buildEventProps(selectedPasuk),
      file_name: file.name,
      file_type: file.type,
      file_size: file.size,
    });
    e.target.value = '';
  };

  const handleSend = async () => {
    if (!selectedPasuk) return;
    const pasukId = selectedPasuk.id;
    const pendingFile = pendingFiles[pasukId];
    const pendingRecordingBlob = pendingRecordingBlobs[pasukId];

    setSavingPasukId(pasukId);

    if (pendingFile) {
      setStatusMessage(t('rabbi_upload_uploading_file') as string);
      setStatusType('loading');
      try {
        trackRabbiAudioEvent('upload_start', {
          ...buildEventProps(selectedPasuk),
          source: 'file',
          file_name: pendingFile.name,
          file_type: pendingFile.type,
          file_size: pendingFile.size,
        });
        const url = await uploadRabbiAudioFile(
          selectedPasuk.book,
          selectedPasuk.chapter.toString(),
          selectedPasuk.pasuk,
          pendingFile,
        );
        if (url?.length > 0) {
          setStatusMessage(t('rabbi_upload_uploaded_success') as string);
          setStatusType('success');
          trackRabbiAudioEvent('upload_success', {
            ...buildEventProps(selectedPasuk),
            source: 'file',
            file_name: pendingFile.name,
            file_type: pendingFile.type,
            file_size: pendingFile.size,
            url,
          });
          setPsukim((prev) =>
            prev.map((p) => (p.id === selectedPasuk.id ? { ...p, audioUrl: url } : p)),
          );
          // Update S3 existence map and invalidate cache so next check refetches
          setS3ExistenceMap((prev) => ({
            ...prev,
            [selectedPasuk.id]: true,
          }));
          invalidateS3CacheRef.current = true;
          setPendingFiles((prev) => {
            const newFiles = { ...prev };
            delete newFiles[pasukId];
            return newFiles;
          });
        }
      } catch (err) {
        console.error(err);
        setStatusMessage(t('rabbi_upload_upload_failed') as string);
        setStatusType('error');
        trackRabbiAudioEvent('upload_error', {
          ...buildEventProps(selectedPasuk),
          source: 'file',
          file_name: pendingFile.name,
          file_type: pendingFile.type,
          file_size: pendingFile.size,
          error: err instanceof Error ? err.message : String(err),
        });
      } finally {
        setSavingPasukId(null);
      }
    } else if (pendingRecordingBlob) {
      setStatusMessage(t('rabbi_upload_uploading_recording') as string);
      setStatusType('loading');
      try {
        trackRabbiAudioEvent('upload_start', {
          ...buildEventProps(selectedPasuk),
          source: 'recording',
          file_type: pendingRecordingBlob.type,
          file_size: pendingRecordingBlob.size,
        });
        const url = await uploadRabbiPasukAudioFromBlob(
          selectedPasuk.book,
          selectedPasuk.chapter.toString(),
          selectedPasuk.pasuk,
          pendingRecordingBlob,
        );
        if (url?.length) {
          setStatusMessage(t('rabbi_upload_uploaded_success') as string);
          setStatusType('success');
          trackRabbiAudioEvent('upload_success', {
            ...buildEventProps(selectedPasuk),
            source: 'recording',
            file_type: pendingRecordingBlob.type,
            file_size: pendingRecordingBlob.size,
            url,
          });
          setPsukim((prev) =>
            prev.map((p) => (p.id === selectedPasuk.id ? { ...p, audioUrl: url } : p)),
          );
          // Update S3 existence map and invalidate cache so next check refetches
          setS3ExistenceMap((prev) => ({
            ...prev,
            [selectedPasuk.id]: true,
          }));
          invalidateS3CacheRef.current = true;
          setPendingRecordingBlobs((prev) => {
            const newBlobs = { ...prev };
            delete newBlobs[pasukId];
            return newBlobs;
          });
          setShouldCheckS3(true); // refresh existence map after successful upload
        }
      } catch (err) {
        console.error(err);
        setStatusMessage(t('rabbi_upload_upload_failed') as string);
        setStatusType('error');
        trackRabbiAudioEvent('upload_error', {
          ...buildEventProps(selectedPasuk),
          source: 'recording',
          file_type: pendingRecordingBlob.type,
          file_size: pendingRecordingBlob.size,
          error: err instanceof Error ? err.message : String(err),
        });
      } finally {
        setSavingPasukId(null);
      }
    } else {
      setSavingPasukId(null);
    }
  };

  const handlePlayback = (pasukId: number) => {
    const targetPasuk = psukim.find((p) => p.id === pasukId);
    const audio = audioRefs.current[pasukId];
    if (audio) {
      if (!audio.src) {
        setStatusMessage(t('rabbi_upload_audio_unavailable') || 'Audio not available');
        setStatusType('error');
        return;
      }
      if (playingAudioId === pasukId && !audio.paused) {
        audio.pause();
        setPlayingAudioId(null);
        trackAudioPlayback('pause', 'rabbi', pasukId);
        trackRabbiAudioEvent('pause', buildEventProps(targetPasuk));
      } else {
        // Stop any other playing audio
        Object.keys(audioRefs.current).forEach((id) => {
          const numId = Number(id);
          if (numId !== pasukId && audioRefs.current[numId]) {
            audioRefs.current[numId]?.pause();
            audioRefs.current[numId]!.currentTime = 0;
          }
        });
        audio
          .play()
          .then(() => {
            setPlayingAudioId(pasukId);
            trackAudioPlayback('play', 'rabbi', pasukId);
            trackRabbiAudioEvent('play', buildEventProps(targetPasuk));
          })
          .catch((err) => {
            console.error('Audio play failed', err);
            setPlayingAudioId(null);
            trackRabbiAudioEvent('play_error', {
              ...buildEventProps(targetPasuk),
              error: err instanceof Error ? err.message : String(err),
            });
            setStatusMessage(t('rabbi_upload_audio_unavailable') || 'Audio not available');
            setStatusType('error');
          });
      }
    }
  };

  const handleDelete = async (pasuk: TorahPasuk) => {
    setStatusMessage(t('rabbi_upload_deleting_audio') as string);
    setStatusType('loading');
    trackRabbiAudioEvent('delete_start', buildEventProps(pasuk));
    try {
      // Get bearer token from localStorage
      const token = localStorage.getItem('rabbiToken');
      if (!token) {
        throw new Error('No authentication token found. Please login first.');
      }

      // Call the delete API endpoint
      const DELETE_ENDPOINT = FIREBASE_FUNCTIONS.deleteRabbiAudio();
      const resp = await callCloudFunction('deleteRabbiAudio', DELETE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          book: pasuk.book,
          chapter: pasuk.chapter,
          pasuk: pasuk.pasuk,
        }),
      });

      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || 'Delete failed');
      }

      // Clear local state after successful deletion; invalidate cache so next check refetches
      setPsukim((prev) => prev.map((p) => (p.id === pasuk.id ? { ...p, audioUrl: undefined } : p)));
      setS3ExistenceMap((prev) => ({
        ...prev,
        [pasuk.id]: false,
      }));
      invalidateS3CacheRef.current = true;

      // Clear pending files for this pasuk
      setPendingFiles((prev) => {
        const newFiles = { ...prev };
        delete newFiles[pasuk.id];
        return newFiles;
      });
      setPendingRecordingBlobs((prev) => {
        const newBlobs = { ...prev };
        delete newBlobs[pasuk.id];
        return newBlobs;
      });
      setShouldCheckS3(true); // refresh existence after delete

      // Stop audio if playing
      const audio = audioRefs.current[pasuk.id];
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
        if (playingAudioId === pasuk.id) {
          setPlayingAudioId(null);
        }
      }

      // Cleanup object URL if exists
      if (objectUrlRefs.current[pasuk.id]) {
        URL.revokeObjectURL(objectUrlRefs.current[pasuk.id]);
        delete objectUrlRefs.current[pasuk.id];
      }

      trackRabbiAudioEvent('delete_success', buildEventProps(pasuk));
      setStatusMessage(t('rabbi_upload_audio_deleted') as string);
      setStatusType('success');
    } catch (err) {
      console.error(err);
      setStatusMessage(t('rabbi_upload_delete_failed') as string);
      setStatusType('error');
      trackRabbiAudioEvent('delete_error', {
        ...buildEventProps(pasuk),
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const handleReRecord = (pasuk: TorahPasuk) => {
    setSelectedPasuk(pasuk);
    // Clear any pending files/recordings for this pasuk
    setPendingFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[pasuk.id];
      return newFiles;
    });
    setPendingRecordingBlobs((prev) => {
      const newBlobs = { ...prev };
      delete newBlobs[pasuk.id];
      return newBlobs;
    });
    setStatusMessage('');
    // Start recording
    startRecording(pasuk);
  };

  const getParashaLabel = (eng: string) => {
    if (isHebrew) {
      return parashaNameMap[eng] || eng;
    }
    return parashaNameMap[eng] ? `${parashaNameMap[eng]} (${eng})` : eng;
  };

  const handleParashaSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (!value) {
      setSelectedParasha('');
      setSelectedPasuk(null);
      setStatusMessage('');
      return;
    }
    setSelectedParasha(value);
    setSelectedPasuk(null);
    setStatusMessage('');
    trackParashaSelection(value, parashaNameMap[value] || value);
    trackRabbiAudioEvent('parasha_selected', {
      parasha: value,
      parasha_hebrew: parashaNameMap[value] || value,
      rabbi_id: rabbiInfo?.id || rabbiInfo?.key,
      rabbi_nusach: rabbiInfo?.nusach,
    });
  };

  return (
    <div
      style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
      className="bg-[#f6f5f1] text-[#132a49] flex flex-col items-start"
    >
      {checkingS3 && (
        <SpinnerOverlay message={t('rabbi_upload_loading_files') || 'Loading files...'} />
      )}
      <div className="px-6 pt-6 pb-4 shadow-md bg-[#132a49] text-white text-center w-full flex flex-col items-center justify-center">
        <div>
          <h1 className="text-xl font-semibold">
            {t('rabbi_upload_title') || 'Upload the Psukim'}
          </h1>
          <p className="text-xs opacity-80 mt-1">
            {t('rabbi_upload_subtitle') || 'This page is for uploading the psukim recordings'}
          </p>
        </div>
      </div>
      <div className="p-6 w-full max-w-2xl mx-auto">
        <select
          value={selectedParasha}
          onChange={handleParashaSelect}
          className="w-full py-3 px-4 rounded-2xl border-2 border-[#caa42e] text-sm bg-white text-[#132a49] cursor-pointer appearance-none focus:outline-none focus:ring-2 focus:ring-[#caa42e]"
          style={{
            textAlign: isHebrew ? 'right' : 'left',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23132a49' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 14px center',
            paddingRight: '36px',
          }}
        >
          <option value="">{t('rabbi_upload_parasha_placeholder') || 'Select a parasha...'}</option>
          {parashotByBook.map((book) => (
            <optgroup
              key={book.bookEnglish}
              label={`━━  📖 ${isHebrew ? book.bookHebrew : book.bookEnglish}  ━━`}
              style={{
                fontWeight: 900,
                fontSize: 15,
                color: '#132a49',
                backgroundColor: '#F1E9D3',
                padding: '12px 8px',
                marginTop: '8px',
              }}
            >
              {book.parashot.map((p: any) => (
                <option
                  key={p.english}
                  value={p.english}
                  style={{
                    fontWeight: 400,
                    color: '#132a49',
                    padding: '8px 0',
                  }}
                >
                  {isHebrew ? p.hebrew : p.english}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <input
        type="file"
        accept="audio/*"
        ref={specialFileInputRef}
        onChange={handleSpecialFileUpload}
        className="hidden"
        id="special-audio-file-input"
      />
      <div className="flex-1 overflow-y-auto px-4 md:px-8 pb-32">
        <div className="max-w-6xl mx-auto w-full">
          {/* קריאת שמע וברכות התורה */}
          <div className="mb-8">
            <h2
              className={`text-lg md:text-2xl font-semibold mb-4 flex items-center gap-3 ${
                isHebrew ? ' text-right' : 'justify-start text-left'
              }`}
              dir={isHebrew ? 'rtl' : 'ltr'}
            >
              {t('rabbi_upload_shema_birchot_section', 'Kriat Shema & Birchot HaTorah')}
            </h2>
            <div className="space-y-4">
              {(['shema', 'birchot_hatorah'] as RabbiSpecialAudioType[]).map((type) => {
                const label =
                  type === 'shema'
                    ? t('rabbi_upload_shema', 'Kriat Shema')
                    : t('rabbi_upload_birchot_hatorah', 'Birchot HaTorah');
                const hasAudio =
                  pendingSpecialFiles[type] || pendingSpecialBlobs[type] || s3ExistenceMap[type];
                const isRecordingThis = recordingSpecial === type;
                let audioUrl: string | undefined;
                if (pendingSpecialBlobs[type]) {
                  if (!specialObjectUrlRefs.current[type]) {
                    specialObjectUrlRefs.current[type] = URL.createObjectURL(
                      pendingSpecialBlobs[type]!,
                    );
                  }
                  audioUrl = specialObjectUrlRefs.current[type];
                } else if (s3ExistenceMap[type] && rabbiInfo) {
                  audioUrl = getRabbiSpecialAudioUrl(
                    type,
                    rabbiInfo.nusach?.toLowerCase() || 'ashkenaz',
                    rabbiInfo.key || rabbiInfo.id || '',
                  );
                } else if (pendingSpecialFiles[type]) {
                  if (!specialObjectUrlRefs.current[type]) {
                    specialObjectUrlRefs.current[type] = URL.createObjectURL(
                      pendingSpecialFiles[type]!,
                    );
                  }
                  audioUrl = specialObjectUrlRefs.current[type];
                }
                return (
                  <div
                    key={type}
                    className="rounded-2xl border border-[#e2e0d9] bg-white shadow-md p-5"
                  >
                    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                      <div className="font-semibold text-[#132a49]" dir="rtl">
                        {label}
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate(type === 'shema' ? '/shema' : '/birchot-hatorah')}
                        className="text-sm font-medium text-[#132a49] underline hover:no-underline flex items-center gap-1"
                      >
                        {isHebrew ? '← ' : ''}
                        {t('rabbi_upload_view_text', 'View text')}
                        {isHebrew ? '' : ' →'}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center w-full">
                      {!hasAudio ? (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              if (isRecordingThis) {
                                stopRecordingSpecial();
                              } else {
                                startRecordingSpecial(type);
                              }
                            }}
                            className={`flex-1 min-w-0 px-4 py-3 rounded-lg border flex items-center justify-center gap-2 font-medium text-sm ${
                              isRecordingThis
                                ? 'bg-red-600 border-red-600 text-white animate-pulse'
                                : 'bg-[#132a49] border-[#132a49] text-white hover:bg-[#0f2239]'
                            }`}
                          >
                            {isRecordingThis ? t('stop') : t('rabbi_upload_record')}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setSpecialFileUploadTarget(type);
                              specialFileInputRef.current?.click();
                            }}
                            className="flex-1 min-w-0 px-4 py-3 rounded-lg border bg-[#caa42e] border-[#caa42e] text-[#132a49] hover:bg-[#b69320] flex items-center justify-center gap-2 font-medium text-sm"
                          >
                            {t('rabbi_upload_upload')}
                          </button>
                        </>
                      ) : (
                        <>
                          {audioUrl && (
                            <>
                              <button
                                type="button"
                                onClick={() => handlePlaybackSpecial(type)}
                                className="w-10 h-10 flex-shrink-0 rounded-full bg-[#132a49] text-white flex items-center justify-center hover:bg-[#0f2239]"
                              >
                                {playingSpecial === type ? <span>⏸</span> : <span>▶</span>}
                              </button>
                              <audio
                                ref={(el) => {
                                  specialAudioRefs.current[type] = el;
                                }}
                                src={audioUrl}
                                onEnded={() => setPlayingSpecial(null)}
                                className="hidden"
                              />
                            </>
                          )}
                          {!s3ExistenceMap[type] && (
                            <button
                              type="button"
                              onClick={() => handleSendSpecial(type)}
                              disabled={savingSpecial === type}
                              className="flex-1 min-w-0 px-4 py-2.5 rounded-lg bg-[#132a49] border border-[#132a49] text-white font-medium text-sm hover:bg-[#0f2239] disabled:opacity-70 flex items-center justify-center"
                            >
                              {savingSpecial === type
                                ? t('rabbi_upload_saving')
                                : t('rabbi_upload_save')}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              if (isRecordingThis) stopRecordingSpecial();
                              else startRecordingSpecial(type);
                            }}
                            className="flex-1 min-w-0 px-4 py-2.5 rounded-lg bg-[#caa42e] border border-[#caa42e] text-[#132a49] font-medium text-sm hover:bg-[#b69320] flex items-center justify-center"
                          >
                            {isRecordingThis ? t('stop') : t('rabbi_upload_re_record')}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setSpecialFileUploadTarget(type);
                              specialFileInputRef.current?.click();
                            }}
                            className="flex-1 min-w-0 px-4 py-2 rounded-lg border border-[#caa42e] text-[#132a49] text-sm flex items-center justify-center"
                          >
                            {t('rabbi_upload_upload')}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteSpecial(type)}
                            className="flex-1 min-w-0 px-4 py-2 rounded-lg border border-red-500 text-red-700 text-sm hover:bg-red-50 flex items-center justify-center"
                          >
                            {t('rabbi_upload_delete')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {selectedParasha && (
            <h2
              className={`text-lg md:text-2xl font-semibold mb-4 flex items-center gap-3 ${
                isHebrew ? ' text-right' : 'justify-start text-left'
              }`}
              dir={isHebrew ? 'rtl' : 'ltr'}
            >
              <span>{getParashaLabel(selectedParasha)}</span>
              <span className="text-xs md:text-sm opacity-60">
                {psukim.length} {t('rabbi_upload_psukim')}
              </span>
            </h2>
          )}
          {psukim.length === 0 && selectedParasha && (
            <div className="text-sm text-gray-500">{t('rabbi_upload_no_psukim')}</div>
          )}
          <div className="space-y-3 md:space-y-4">
            {psukim.map((_pasuk) => {
              const active = selectedPasuk?.id === _pasuk.id;
              return (
                <div
                  key={_pasuk.id}
                  className={`relative rounded-2xl border transition shadow-md group ${
                    active ? 'border-[#caa42e] bg-[#fff9e6]' : 'border-[#e2e0d9] bg-white'
                  } hover:border-[#caa42e]`}
                >
                  <button
                    onClick={() => {
                      setSelectedPasuk(_pasuk);
                      // Clear status message when selecting a different pasuk
                      if (selectedPasuk?.id !== _pasuk.id) {
                        setStatusMessage('');
                      }
                    }}
                    className="w-full px-5 pt-4 pb-2"
                  >
                    <div
                      className="flex items-start justify-between gap-4"
                      dir={isHebrew ? 'rtl' : 'ltr'}
                    >
                      <div className="flex-1">
                        <div
                          className="text-gray-500 text-sm tracking-wide"
                          dir={isHebrew ? 'ltr' : 'ltr'}
                          style={{
                            fontFamily: '"Noto Serif Hebrew", "David Libre", serif',
                            WebkitFontSmoothing: 'antialiased',
                          }}
                        >
                          {isHebrew ? (
                            <>
                              פרק{' '}
                              <span style={{ fontWeight: 700, color: '#132a49' }}>
                                {numberToHebrewLetters(_pasuk.chapter)}
                              </span>{' '}
                              פסוק{' '}
                              <span style={{ fontWeight: 700, color: '#132a49' }}>
                                {numberToHebrewLetters(_pasuk.pasuk)}
                              </span>
                            </>
                          ) : (
                            <>
                              {_pasuk.book} פרק{' '}
                              <span style={{ fontWeight: 700 }}>{_pasuk.chapter}</span> פסוק{' '}
                              <span style={{ fontWeight: 700 }}>{_pasuk.pasuk}</span>
                            </>
                          )}
                        </div>
                        <div
                          className="mt-1.5 text-lg leading-relaxed"
                          dir="rtl"
                          style={{
                            fontFamily: '"Noto Serif Hebrew", "David Libre", serif',
                            fontSynthesis: 'none',
                            WebkitFontSmoothing: 'antialiased',
                          }}
                        >
                          {renderHebrewWithShvaNa(_pasuk.hebrew)}
                        </div>
                      </div>
                      <div className="ml-4 flex items-center gap-2">
                        {checkingS3 && s3ExistenceMap[_pasuk.id] === undefined ? (
                          ''
                        ) : s3ExistenceMap[_pasuk.id] === true ? (
                          <svg
                            className="w-5 h-5 text-green-600"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          ''
                        )}
                      </div>
                    </div>
                  </button>
                  {/* Action bar */}
                  <div className="px-5 pb-4 border-t border-[#e2e0d9] pt-2">
                    {/* Determine if pasuk has audio (pending or uploaded) */}
                    {(() => {
                      const pendingFile = pendingFiles[_pasuk.id];
                      const pendingRecordingBlob = pendingRecordingBlobs[_pasuk.id];
                      const hasAudio =
                        pendingFile ||
                        pendingRecordingBlob ||
                        _pasuk.audioUrl ||
                        s3ExistenceMap[_pasuk.id];

                      // Get or create object URL for pending files
                      let audioUrl = _pasuk.audioUrl;
                      if (!audioUrl) {
                        if (pendingRecordingBlob) {
                          if (!objectUrlRefs.current[_pasuk.id]) {
                            objectUrlRefs.current[_pasuk.id] =
                              URL.createObjectURL(pendingRecordingBlob);
                          }
                          audioUrl = objectUrlRefs.current[_pasuk.id];
                        } else if (pendingFile) {
                          if (!objectUrlRefs.current[_pasuk.id]) {
                            objectUrlRefs.current[_pasuk.id] = URL.createObjectURL(pendingFile);
                          }
                          audioUrl = objectUrlRefs.current[_pasuk.id];
                        }
                      }

                      // State 1: Before recording/uploading
                      if (!hasAudio) {
                        return (
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                if (active && isRecording) {
                                  stopRecording();
                                } else {
                                  // If not selected, select it and start recording
                                  // If selected but not recording, just start recording
                                  startRecording(_pasuk);
                                }
                              }}
                              className={`flex-1 px-5 py-3 rounded-lg border flex items-center justify-center gap-2 font-medium text-sm ${
                                isRecording && active
                                  ? 'bg-red-600 border-red-600 text-white animate-pulse'
                                  : 'bg-[#132a49] border-[#132a49] text-white hover:bg-[#0f2239]'
                              }`}
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                {isRecording && active ? (
                                  <path
                                    fillRule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                                    clipRule="evenodd"
                                  />
                                ) : (
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                                    clipRule="evenodd"
                                  />
                                )}
                              </svg>
                              <span>
                                {isRecording && active ? t('stop') : t('rabbi_upload_record')}
                              </span>
                            </button>
                            <button
                              onClick={() => {
                                setSelectedPasuk(_pasuk);
                                fileInputRef.current?.click();
                              }}
                              className="flex-1 px-5 py-3 rounded-lg border bg-[#caa42e] border-[#caa42e] text-[#132a49] hover:bg-[#b69320] flex items-center justify-center gap-2 font-medium text-sm"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                              </svg>
                              <span>{t('rabbi_upload_upload')}</span>
                            </button>
                          </div>
                        );
                      }

                      // State 2: After recording/uploading
                      return (
                        <>
                          {/* Audio player bar */}
                          {audioUrl && (
                            <div className="mb-2 bg-white rounded-lg p-2 border border-[#e2e0d9]">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-gray-200 rounded-full h-1.5 relative">
                                  <div
                                    className="bg-[#132a49] h-1.5 rounded-full"
                                    style={{ width: '0%' }}
                                  ></div>
                                </div>
                                <button
                                  onClick={() => {
                                    handlePlayback(_pasuk.id);
                                  }}
                                  className="w-8 h-8 p-0 rounded-full bg-[#132a49] text-white flex items-center justify-center hover:bg-[#0f2239]"
                                >
                                  {playingAudioId === _pasuk.id ? (
                                    <svg
                                      className="w-4 h-4"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  ) : (
                                    <svg
                                      className="w-4 h-4"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                    </svg>
                                  )}
                                </button>
                                <span className="text-xs text-gray-500">0:00 / 0:00</span>
                              </div>
                              <audio
                                ref={(el) => {
                                  audioRefs.current[_pasuk.id] = el;
                                }}
                                src={audioUrl}
                                onEnded={() => setPlayingAudioId(null)}
                                onError={() => {
                                  setPlayingAudioId(null);
                                  setStatusMessage(
                                    t('rabbi_upload_audio_unavailable') || 'Audio not available',
                                  );
                                  setStatusType('error');
                                }}
                                onTimeUpdate={(e) => {
                                  const audio = e.currentTarget;
                                  const progressBar = e.currentTarget.parentElement?.querySelector(
                                    '.bg-\\[\\#132a49\\]',
                                  ) as HTMLElement;
                                  const timeDisplay = e.currentTarget.parentElement?.querySelector(
                                    '.text-xs',
                                  ) as HTMLElement;
                                  if (progressBar && timeDisplay && audio.duration) {
                                    const percent = (audio.currentTime / audio.duration) * 100;
                                    progressBar.style.width = `${percent}%`;
                                    const currentMins = Math.floor(audio.currentTime / 60);
                                    const currentSecs = Math.floor(audio.currentTime % 60);
                                    const totalMins = Math.floor(audio.duration / 60);
                                    const totalSecs = Math.floor(audio.duration % 60);
                                    timeDisplay.textContent = `${currentMins}:${currentSecs
                                      .toString()
                                      .padStart(2, '0')} / ${totalMins}:${totalSecs
                                      .toString()
                                      .padStart(2, '0')}`;
                                  }
                                }}
                                className="hidden"
                              />
                            </div>
                          )}

                          {/* Action buttons - Top row: Re-record or Stop button */}
                          <div className="flex gap-2 mb-2">
                            {isRecording && active ? (
                              /* Stop button - when recording is in progress */
                              <button
                                onClick={() => stopRecording()}
                                className="flex-1 px-5 py-3 rounded-lg bg-red-600 border border-red-600 text-white animate-pulse flex items-center justify-center gap-2 font-medium text-sm"
                              >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span>{t('stop')}</span>
                              </button>
                            ) : (
                              /* Re-record button - Large, Golden */
                              <button
                                onClick={() => handleReRecord(_pasuk)}
                                className="flex-1 px-5 py-3 rounded-lg bg-[#caa42e] border border-[#caa42e] text-[#132a49] hover:bg-[#b69320] flex items-center justify-center gap-2 font-medium text-sm"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                  />
                                </svg>
                                <span>{t('rabbi_upload_re_record')}</span>
                              </button>
                            )}
                          </div>

                          {/* Middle row: Upload and Save buttons */}
                          <div className="flex gap-2 items-center">
                            {/* Upload button - Small, Golden */}
                            <button
                              onClick={() => {
                                setSelectedPasuk(_pasuk);
                                fileInputRef.current?.click();
                              }}
                              className="rounded-lg bg-transparent border border-[#caa42e] text-[#132a49] hover:bg-[#caa42e]/10 flex items-center justify-center p-2"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                              </svg>
                            </button>
                            {/* Save button - Dark Blue */}
                            <button
                              onClick={() => {
                                setSelectedPasuk(_pasuk);
                                handleSend();
                              }}
                              disabled={
                                savingPasukId === _pasuk.id ||
                                !active ||
                                (!pendingFiles[_pasuk.id] && !pendingRecordingBlobs[_pasuk.id])
                              }
                              className={`flex-1 py-2.5 rounded-lg border flex items-center justify-center gap-2 font-medium text-sm ${
                                savingPasukId === _pasuk.id
                                  ? 'bg-[#132a49] border-[#132a49] text-white cursor-wait opacity-80'
                                  : active &&
                                      (pendingFiles[_pasuk.id] || pendingRecordingBlobs[_pasuk.id])
                                    ? 'bg-[#132a49] border-[#132a49] text-white hover:bg-[#0f2239] cursor-pointer'
                                    : 'bg-gray-300 border-gray-300 text-gray-500 cursor-not-allowed'
                              }`}
                            >
                              {savingPasukId === _pasuk.id ? (
                                <svg
                                  className="w-4 h-4 animate-spin"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" />
                                </svg>
                              )}
                              <span>
                                {savingPasukId === _pasuk.id
                                  ? t('rabbi_upload_saving')
                                  : t('rabbi_upload_save')}
                              </span>
                            </button>
                          </div>

                          {/* Bottom row: Delete button */}
                          <div className="flex gap-2 items-center mt-2">
                            <button
                              onClick={() => {
                                setSelectedPasuk(_pasuk);
                                handleDelete(_pasuk);
                              }}
                              className="flex-1 py-2 rounded-lg bg-transparent border border-red-500 text-red-700 hover:bg-red-500/10 flex items-center justify-center gap-2 text-sm"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <span>{t('rabbi_upload_delete')}</span>
                            </button>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  {/* Status message for this pasuk */}
                  {active && statusMessage && (
                    <div
                      className={`mx-5 mb-3 text-xs font-semibold rounded-md shadow-sm py-1.5 px-2.5 border ${
                        statusType === 'success'
                          ? 'bg-green-600 border-green-600 text-white'
                          : statusType === 'error'
                            ? 'bg-red-600 border-red-600 text-white'
                            : statusType === 'loading'
                              ? 'bg-[#caa42e] border-[#caa42e] text-white animate-pulse'
                              : 'bg-[#132a49] border-[#132a49] text-white'
                      }`}
                    >
                      {statusMessage}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <input
        type="file"
        accept="audio/*"
        ref={fileInputRef}
        onChange={handleFileUpload}
        className="hidden"
      />
      <BottomNav />
    </div>
  );
};

export default RabbiUploadPage;
